import React, { useCallback, useRef } from "react";

import _ from "lodash";

import { Input, Text } from "@mantine/core";
import { isWidgetReadOnly } from "../helpers/formReadOnly";

import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import ImageCompress from "quill-image-compress";

import "./richTextEditor.css";
Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageCompress", ImageCompress);

window.Quill = Quill;

/**
 * ReactQuill remounts when `modules` / `formats` are new object identities each render
 * (common on mobile: full parent re-renders → visible flicker). Keep one stable config.
 */
const RICH_TEXT_TOOLBAR = [
  [{ header: [1, 2, false] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  ["clean"],
];

const RICH_TEXT_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "link",
  "image",
];

const RICH_TEXT_MODULES = {
  toolbar: RICH_TEXT_TOOLBAR,
  imageResize: {
    parchment: Quill.import("parchment"),
    modules: ["Resize", "DisplaySize", "Toolbar"],
  },
  imageCompress: {
    quality: 0.7,
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: "image/jpeg",
    debug: false,
  },
};

const isBlankRichText = (html) => {
  if (!html || !String(html).trim()) return true;
  const text = String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return !text;
};

const RichTextEditor = ({
  form,
  name,
  label,
  mt = "xs",
  minRows,
  readOnly = false,
  readonly = false,
  disabled = false,
}) => {
  const quillRef = useRef(null);
  const skipMountChangeRef = useRef(true);
  const c = _.get(form.values, name);
  const value = typeof c === "string" ? c : c == null ? "" : String(c);
  const editorStyle =
    minRows > 0
      ? { ["--rte-min-rows"]: minRows }
      : undefined;

  const setQuillRef = useCallback((el) => {
    quillRef.current = el;
  }, []);

  const handleEditorChange = useCallback(
    (content) => {
      if (skipMountChangeRef.current) {
        skipMountChangeRef.current = false;
        return;
      }
      form.markUserEdited?.();
      form.setFieldValue(name, content);
    },
    [form, name],
  );

  if (isWidgetReadOnly({ disabled, readOnly, readonly, form })) {
    return (
      <div style={{ marginTop: mt }}>
        {label ? (
          <Text size="xs" color="dimmed">
            {label}
          </Text>
        ) : null}
        {isBlankRichText(value) ? (
          <Text size="sm" weight={600}>
            -
          </Text>
        ) : (
          <div className="ql-snow">
            <div
              className="ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={minRows > 0 ? "rich-text-editor-sized" : undefined} style={editorStyle}>
      <Input.Wrapper label={label} mt={mt} />
      <ReactQuill
        ref={setQuillRef}
        theme="snow"
        className="black-theme"
        value={value}
        onChange={handleEditorChange}
        placeholder="Write something..."
        formats={RICH_TEXT_FORMATS}
        modules={RICH_TEXT_MODULES}
      />
    </div>
  );
};

export default RichTextEditor;
